/**
 * Fazla Mesai (Overtime) Servisi
 * 
 * Bu servis, fazla mesai yönetimi için backend API'leri ile iletişimi sağlar.
 * Singleton pattern kullanılarak tek bir instance oluşturulur.
 * 
 * Özellikler:
 * - Bekleyen talepleri getirme
 * - Tüm overtime kayıtlarını getirme (filtreleme ile)
 * - Dashboard özet verisi
 * - Talep onaylama/reddetme
 * - Çalışana fazla mesai tanımlama
 * - Kendi taleplerim (çalışan için)
 * - Talep oluşturma (çalışan için)
 */

import apiService from '../api/api.service';
import { 
  OvertimeDto, 
  OvertimeCreateDTO, 
  OvertimeReviewDTO, 
  OvertimeSummaryDto,
  OvertimeFilters,
  OvertimeListResponseDto,
  EmployeeOvertimeRequestDTO
} from '../../types/overtime.types';

class OvertimeService {
  private static instance: OvertimeService;

  private constructor() {}

  /**
   * Singleton instance'ı döndürür
   */
  public static getInstance(): OvertimeService {
    if (!OvertimeService.instance) {
      OvertimeService.instance = new OvertimeService();
    }
    return OvertimeService.instance;
  }

  /**
   * Bekleyen fazla mesai taleplerini getirir (Yönetici için)
   * @param filters - Filtreleme parametreleri (departman, tarih aralığı)
   * @returns Bekleyen overtime kayıtları listesi
   */
  public async getPendingOvertimes(filters?: OvertimeFilters): Promise<OvertimeDto[]> {
    const params = new URLSearchParams();
    
    if (filters?.departmentId) {
      params.append('departmentId', filters.departmentId.toString());
    }
    if (filters?.startDate) {
      params.append('startDate', filters.startDate);
    }
    if (filters?.endDate) {
      params.append('endDate', filters.endDate);
    }
    
    const queryString = params.toString();
    const url = queryString ? `/overtime/pending?${queryString}` : '/overtime/pending';
    
    return apiService.get(url);
  }

  /**
   * Tüm overtime kayıtlarını getirir (Yönetici için - Geçmiş)
   * Filtreleme, pagination ve sıralama destekler
   * @param filters - Filtreleme parametreleri
   * @returns Overtime kayıtları ve pagination bilgisi
   */
  public async getAllOvertimes(filters?: OvertimeFilters): Promise<OvertimeListResponseDto> {
    const params = new URLSearchParams();
    
    if (filters?.startDate) {
      params.append('startDate', filters.startDate);
    }
    if (filters?.endDate) {
      params.append('endDate', filters.endDate);
    }
    if (filters?.departmentId) {
      params.append('departmentId', filters.departmentId.toString());
    }
    if (filters?.employeeId) {
      params.append('employeeId', filters.employeeId.toString());
    }
    if (filters?.status && filters.status !== 'all') {
      params.append('status', filters.status);
    }
    if (filters?.page) {
      params.append('page', filters.page.toString());
    }
    if (filters?.perPage) {
      params.append('perPage', filters.perPage.toString());
    }
    
    const queryString = params.toString();
    const url = queryString ? `/overtime/all?${queryString}` : '/overtime/all';
    
    return apiService.get(url);
  }

  /**
   * Dashboard için özet verisi getirir
   * Bekleyen talepler, onaylanan/reddedilen sayılar, son talepler vb.
   * @param period - Zaman periyodu (gün, hafta, ay, yıl)
   * @returns Dashboard özet verisi
   */
  public async getOvertimeSummary(
    period: 'day' | 'week' | 'month' | 'year' = 'month'
  ): Promise<OvertimeSummaryDto> {
    return apiService.get(`/overtime/summary?period=${period}`);
  }

  /**
   * Fazla mesai talebini onayla veya reddet (Yönetici için)
   * @param id - Overtime kaydı ID'si
   * @param data - Onay/red bilgisi ve red nedeni
   * @returns Güncellenmiş overtime kaydı
   */
  public async reviewOvertime(id: number, data: OvertimeReviewDTO): Promise<OvertimeDto> {
    return apiService.put(`/overtime/${id}/review`, data);
  }

  /**
   * Çalışana fazla mesai tanımlar (Yönetici için)
   * Yönetici, bir çalışana direkt fazla mesai tanımlayabilir.
   * autoApprove: true ise direkt onaylı olarak oluşturulur.
   * @param data - Fazla mesai tanımlama bilgileri
   * @returns Oluşturulan overtime kaydı
   */
  public async createOvertimeForEmployee(data: OvertimeCreateDTO): Promise<OvertimeDto> {
    return apiService.post('/overtime/create-for-employee', data);
  }

  /**
   * Kendi fazla mesai taleplerimizi getirir (Çalışan için)
   * @returns Kendi overtime kayıtları listesi
   */
  public async getMyOvertimes(): Promise<OvertimeDto[]> {
    return apiService.get('/overtime/my-requests');
  }

  /**
   * Yeni fazla mesai talebi oluşturur (Çalışan için)
   * Çalışan kendi adına talep oluşturur, yönetici onayı bekler.
   * @param data - Talep bilgileri (tarih, saat, açıklama)
   * @returns Oluşturulan overtime kaydı
   */
  public async createOvertimeRequest(data: EmployeeOvertimeRequestDTO): Promise<OvertimeDto> {
    return apiService.post('/overtime/request', data);
  }

  /**
   * Belirli bir overtime kaydının detayını getirir
   * @param id - Overtime kaydı ID'si
   * @returns Overtime kaydı detayı
   */
  public async getOvertimeById(id: number): Promise<OvertimeDto> {
    return apiService.get(`/overtime/${id}`);
  }

  /**
   * Overtime verilerini Excel formatında export eder (Opsiyonel)
   * Not: Bu endpoint henüz backend'de olmayabilir
   * @param filters - Filtreleme parametreleri
   * @returns Excel dosyası blob
   */
  public async exportOvertimesToExcel(filters?: OvertimeFilters): Promise<Blob> {
    const params = new URLSearchParams();
    
    if (filters?.startDate) {
      params.append('startDate', filters.startDate);
    }
    if (filters?.endDate) {
      params.append('endDate', filters.endDate);
    }
    if (filters?.departmentId) {
      params.append('departmentId', filters.departmentId.toString());
    }
    if (filters?.status && filters.status !== 'all') {
      params.append('status', filters.status);
    }
    
    const queryString = params.toString();
    const url = queryString 
      ? `/overtime/export?format=excel&${queryString}` 
      : '/overtime/export?format=excel';
    
    // Blob olarak response alınır
    return apiService.get(url, { responseType: 'blob' });
  }
}

export default OvertimeService.getInstance();


